import React, { useContext, useReducer, useCallback } from 'react';
import CircularProgress, { CircularProgressProps } from '../components/@common/CircularProgress';

type LoadingOptions = Pick<CircularProgressProps, 'loading'>;
interface SetLoadingPayload {
  isLoading: boolean;
  options?: LoadingOptions;
}
type Action = { type: 'SET_LOADING'; payload: SetLoadingPayload };

export type UseLoading = [UseLoadingState, UseLoadingDispatch];
export interface UseLoadingState {
  isLoading: boolean;
  loadingOptions?: LoadingOptions;
}
export interface UseLoadingDispatch {
  setLoading: (isLoading: boolean, options?: LoadingOptions) => void;
}

const getDefaultLoadingOptions = (): LoadingOptions => ({});

export const getDefaultLoadingState = (): UseLoadingState => ({
  isLoading: false,
  loadingOptions: getDefaultLoadingOptions()
});

const resolvePayload = (payload: SetLoadingPayload): UseLoadingState => {
  const { options, ...updates } = payload;
  const loadingOptions = { ...getDefaultLoadingOptions(), ...options };

  return { ...updates, loadingOptions };
};

const loadingReducer = (state: UseLoadingState, action: Action): UseLoadingState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        ...resolvePayload(action.payload)
      };
    default:
      return getDefaultLoadingState();
  }
};

/**
 * UseLoading should be used to display the loading spinner
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LoadingContext = React.createContext<any>([]);

const LoadingProvider: React.FC = props => {
  const [state, dispatch] = useReducer<React.Reducer<UseLoadingState, Action>>(
    loadingReducer,
    getDefaultLoadingState()
  );

  const setLoading = useCallback(
    (isLoading: boolean, options?: LoadingOptions): void => {
      dispatch({ type: 'SET_LOADING', payload: { isLoading, options } });
    },
    [dispatch]
  );

  return (
    <LoadingContext.Provider value={[state, { setLoading }]}>
      <CircularProgress loading={state.isLoading} {...state.loadingOptions} />
      {props.children}
    </LoadingContext.Provider>
  );
};

const useLoading = (): UseLoading => {
  const [state, dispatch] = useContext(LoadingContext);

  return [state, dispatch];
};

export { LoadingProvider, useLoading };
